import { t } from "i18next";
import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import { canv } from "@/canv/global";
import { sleep } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { eventEmitter } from "@/eventEmitter";
import { showSnackbar } from "@/snackbar/snackbar";
import { ImageExport } from "./ImageExport";
import { ImageExportControls } from "./ImageExportControls";
import { ImageExportScreen } from "./ImageExportScreen";
import type { ImageExportFormat } from "./types";

export class ImageExportWindow {
    constructor() {
        this.screen = new ImageExportScreen();
        this.imageExport = new ImageExport();
    }

    open(format: ImageExportFormat = "png") {
        if (this.opened) return;

        this.format = format;

        canv.contextMenu.hide();
        canv.widgetMenu.hide();
        canv.appInterface.exportWindow.close();

        this.opened = true;
        this.screen.create();
        this.createControls();
        this.render();

        eventEmitter.dispatch("toolBox.visibility", { isVisible: false });
        eventEmitter.dispatch("header.button.active.name", {
            name: "export",
        });

        canv.multiManager.changeVisibilityCursor(false);
        canv.toolBox.switchToByName("imageExportTool");
        canv.redraw();
    }

    close = (force = false) => {
        if (!this.opened) return;
        if (this.isExporting && !force) return;

        this.cancelControlsPosition();

        this.root?.unmount();
        this.root = null;

        if (this.controlsWrapper?.parentNode) {
            this.controlsWrapper.parentNode.removeChild(this.controlsWrapper);
            this.controlsWrapper = null;
        }

        this.opened = false;
        this.isExporting = false;
        this.screen.remove();

        eventEmitter.dispatch("toolBox.visibility", { isVisible: true });
        eventEmitter.dispatch("header.button.active.name", {
            name: null,
        });

        if (canv.toolBox.currentTool?.name === "imageExportTool") {
            canv.toolBox.switchToByName("cursorTool");
        }

        if (!canv.multiManager.disableMultiCursors) {
            canv.multiManager.changeVisibilityCursor(true);
        }

        canv.redraw();
    };

    download = async () => {
        if (this.isExporting) return;

        this.isExporting = true;
        this.render();

        eventEmitter.dispatch("button.disabled", {
            export: true,
        });

        const loadingSnackbar = showSnackbar(t("export.loading"), {
            type: "loading",
        });

        await sleep(0);

        try {
            await this.imageExport.exportArea(this.screen.getArea(), this.format);
            showSnackbar(t("export.success"), {
                duration: SNACKBAR_TIME,
                type: "success",
            });
        } catch {
            showSnackbar(t("export.error"), { type: "error", duration: SNACKBAR_TIME });
        } finally {
            loadingSnackbar.close();
            eventEmitter.dispatch("button.disabled", {
                export: false,
            });
            this.close(true);
        }
    };

    render() {
        if (!this.root) return;

        this.root.render(
            createElement(ImageExportControls, {
                format: this.format,
                isExporting: this.isExporting,
                onDownload: this.download,
                onClose: this.close,
            }),
        );

        this.scheduleControlsPosition();
    }

    private createControls() {
        const controlsWrapper = document.createElement("div");
        document.body.appendChild(controlsWrapper);

        this.controlsWrapper = controlsWrapper;
        this.root = createRoot(controlsWrapper);
    }

    private positionControls = () => {
        const controls = this.controlsWrapper?.firstElementChild as HTMLElement | null;
        const leftBar = document.querySelector<HTMLElement>("[data-ui-left-bar]");
        if (!controls || !leftBar) return;

        const leftBarRect = leftBar.getBoundingClientRect();

        controls.style.left = `${leftBarRect.left}px`;
        controls.style.top = `${leftBarRect.bottom + 8}px`;
    };

    private scheduleControlsPosition() {
        this.cancelControlsPosition();

        this.positionControlsRafId = requestAnimationFrame(() => {
            this.positionControlsRafId = null;
            this.positionControls();
        });
    }

    private cancelControlsPosition() {
        if (this.positionControlsRafId === null) return;

        cancelAnimationFrame(this.positionControlsRafId);
        this.positionControlsRafId = null;
    }

    opened = false;
    isExporting = false;
    screen: ImageExportScreen;
    format: ImageExportFormat = "png";

    private root: Root | null = null;
    private controlsWrapper: HTMLElement | null = null;
    private positionControlsRafId: number | null = null;
    private readonly imageExport: ImageExport;
}
